import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  ArrowLeft, 
  Search, 
  Loader2,
  FileText,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  DollarSign
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AccountingDocuments = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [filteredDocs, setFilteredDocs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedDoc, setExpandedDoc] = useState(null);
  const [filters, setFilters] = useState({
    documentType: '',
    dateFrom: '',
    dateTo: ''
  });

  useEffect(() => {
    loadData();
  // eslint-disable-next-line
  }, []);

  useEffect(() => {
    filterDocuments();
  // eslint-disable-next-line
  }, [searchTerm, documents]);

  const loadData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.documentType) params.append('document_type', filters.documentType);
      if (filters.dateFrom) params.append('date_from', filters.dateFrom);
      if (filters.dateTo) params.append('date_to', filters.dateTo);
      params.append('limit', '500');
      
      const response = await axios.get(`${BACKEND_URL}/api/accounting-documents?${params}`);
      setDocuments(response.data);
      setFilteredDocs(response.data);
    } catch (error) {
      console.error('Error loading accounting documents:', error);
      toast.error('Failed to load accounting documents');
    } finally {
      setLoading(false);
    }
  };

  const filterDocuments = () => {
    let filtered = [...documents];
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(d => 
        d.document_number?.toLowerCase().includes(term) ||
        d.reference?.toLowerCase().includes(term) ||
        d.header_text?.toLowerCase().includes(term)
      );
    }
    
    setFilteredDocs(filtered);
  };

  const handleRefresh = () => {
    loadData();
    toast.success('Documents refreshed');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value || 0);
  };

  const getDocTypeLabel = (docType) => {
    const labels = {
      'WE': 'WE - Goods Receipt',
      'RE': 'RE - Invoice Receipt',
      'SA': 'SA - G/L Account',
      'KR': 'KR - Vendor Invoice',
      'KZ': 'KZ - Vendor Payment'
    };
    return labels[docType] || docType;
  };

  const getDocTypeBadge = (docType) => {
    const colors = {
      'WE': 'bg-orange-100 text-orange-800',
      'RE': 'bg-orange-100 text-orange-800',
      'SA': 'bg-gray-100 text-gray-800',
      'KR': 'bg-orange-100 text-orange-800',
      'KZ': 'bg-green-100 text-green-800'
    };
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${colors[docType] || 'bg-gray-100 text-gray-800'}`}>
        {docType}
      </span>
    );
  };

  // Calculate totals
  const totals = filteredDocs.reduce((acc, d) => ({
    debit: acc.debit + (d.total_debit || 0),
    credit: acc.credit + (d.total_credit || 0)
  }), { debit: 0, credit: 0 });

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/inventory')}>
              <ArrowLeft className="h-4 w-4 mr-2" /> Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Accounting Documents</h1>
              <p className="text-sm text-gray-500">FI - Financial Documents</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleRefresh}>
              <RefreshCw className="h-4 w-4 mr-2" /> Refresh
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="text-sm font-medium text-gray-700 mb-1 block">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search by document number, reference..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            <div className="w-48">
              <label className="text-sm font-medium text-gray-700 mb-1 block">Document Type</label>
              <select
                className="w-full border rounded-md px-3 py-2 text-sm"
                value={filters.documentType}
                onChange={(e) => setFilters({ ...filters, documentType: e.target.value })}
              >
                <option value="">All Types</option>
                <option value="WE">WE - Goods Receipt</option>
                <option value="RE">RE - Invoice Receipt</option>
                <option value="SA">SA - G/L Account</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Date From</label>
              <Input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Date To</label>
              <Input
                type="date"
                value={filters.dateTo}
                onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
              />
            </div>
            <Button onClick={loadData}>Apply</Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-orange-500">
            <p className="text-sm text-gray-500">Total Documents</p>
            <p className="text-2xl font-bold text-gray-900">{filteredDocs.length}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-green-500">
            <p className="text-sm text-gray-500">Total Debits</p>
            <p className="text-xl font-bold text-green-700">{formatCurrency(totals.debit)}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-red-500">
            <p className="text-sm text-gray-500">Total Credits</p>
            <p className="text-xl font-bold text-red-700">{formatCurrency(totals.credit)}</p>
          </div>
        </div>

        {/* Documents Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
              <span className="ml-2 text-gray-600">Loading documents...</span>
            </div>
          ) : filteredDocs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <DollarSign className="h-12 w-12 mb-4 opacity-50" />
              <p>No accounting documents found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="w-10"></TableHead>
                  <TableHead>Document No.</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Posting Date</TableHead>
                  <TableHead>Reference</TableHead>
                  <TableHead className="text-right">Total Debit</TableHead>
                  <TableHead className="text-right">Total Credit</TableHead>
                  <TableHead>Created By</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredDocs.map((doc, index) => (
                  <React.Fragment key={index}>
                    <TableRow 
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => setExpandedDoc(expandedDoc === index ? null : index)}
                    >
                      <TableCell>
                        {expandedDoc === index ? 
                          <ChevronUp className="h-4 w-4" /> : 
                          <ChevronDown className="h-4 w-4" />
                        }
                      </TableCell>
                      <TableCell className="font-medium text-orange-600">{doc.document_number}</TableCell>
                      <TableCell>{doc.company_code}</TableCell>
                      <TableCell>{getDocTypeBadge(doc.document_type)}</TableCell>
                      <TableCell>{formatDate(doc.posting_date)}</TableCell>
                      <TableCell>{doc.reference || doc.material_document || doc.invoice_document || '-'}</TableCell>
                      <TableCell className="text-right text-green-700 font-medium">
                        {formatCurrency(doc.total_debit)}
                      </TableCell>
                      <TableCell className="text-right text-red-700 font-medium">
                        {formatCurrency(doc.total_credit)}
                      </TableCell>
                      <TableCell>{doc.created_by}</TableCell>
                    </TableRow>
                    {/* Expanded Line Items */}
                    {expandedDoc === index && doc.items && (
                      <TableRow className="bg-gray-50">
                        <TableCell colSpan={9}>
                          <div className="p-4">
                            <h4 className="font-medium text-gray-700 mb-3">Line Items</h4>
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="text-left text-gray-500 border-b">
                                  <th className="pb-2">Item</th>
                                  <th className="pb-2">PK</th>
                                  <th className="pb-2">Account</th>
                                  <th className="pb-2">Type</th>
                                  <th className="pb-2">D/C</th>
                                  <th className="pb-2 text-right">Amount</th>
                                  <th className="pb-2">Assignment</th>
                                  <th className="pb-2">Text</th>
                                </tr>
                              </thead>
                              <tbody>
                                {doc.items.map((item, idx) => (
                                  <tr key={idx} className="border-b border-gray-100">
                                    <td className="py-2">{item.item_number}</td>
                                    <td className="py-2">{item.posting_key}</td>
                                    <td className="py-2 font-medium">{item.account}</td>
                                    <td className="py-2">
                                      <span className={`px-2 py-0.5 rounded text-xs ${
                                        item.account_type === 'K' ? 'bg-orange-100 text-orange-800' :
                                        item.account_type === 'S' ? 'bg-orange-100 text-orange-800' :
                                        'bg-gray-100 text-gray-800'
                                      }`}>
                                        {item.account_type === 'K' ? 'Vendor' : 
                                         item.account_type === 'S' ? 'G/L' : item.account_type}
                                      </span>
                                    </td>
                                    <td className="py-2">
                                      <span className={`font-medium ${
                                        item.debit_credit === 'S' ? 'text-green-600' : 'text-red-600'
                                      }`}>
                                        {item.debit_credit === 'S' ? 'Debit' : 'Credit'}
                                      </span>
                                    </td>
                                    <td className={`py-2 text-right font-medium ${
                                      item.debit_credit === 'S' ? 'text-green-700' : 'text-red-700'
                                    }`}>
                                      {formatCurrency(item.amount)}
                                    </td>
                                    <td className="py-2 text-gray-600">{item.assignment || '-'}</td>
                                    <td className="py-2 text-gray-600 truncate max-w-[200px]">{item.text || '-'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
        
        <div className="mt-4 text-sm text-gray-500 text-right">
          Showing {filteredDocs.length} documents
        </div>
      </div>
    </Layout>
  );
};

export default AccountingDocuments;
